import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { newsItems, categories } from '../../mocks/news';

const categoryColors: Record<string, string> = {
  'お知らせ': 'border-forest-400/60 text-forest-600 bg-forest-50',
  '活動報告': 'border-gold-500/60 text-gold-600 bg-gold-50/40',
  '入会・協賛': 'border-forest-500/50 text-forest-700 bg-forest-50/60',
  'イベント': 'border-cream-500/60 text-forest-500 bg-cream-100',
};

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState('すべて');

  const filtered = activeCategory === 'すべて'
    ? newsItems
    : newsItems.filter((item) => item.category === activeCategory);

  return (
    <main>
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-20 bg-forest-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/news.jpeg"
            alt="お知らせ"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-800/60 to-forest-800/90" />
        </div>
        {/* Ripple decoration */}
        <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none opacity-10 hidden lg:block">
          <div className="relative w-48 h-48">
            <div className="absolute inset-0 rounded-full border-2 border-cream-200" />
            <div className="absolute inset-5 rounded-full border border-cream-200" />
            <div className="absolute inset-10 rounded-full border border-cream-200" />
            <div className="absolute inset-16 rounded-full border border-cream-200" />
          </div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-cream-400/50" />
            <span className="text-cream-400/60 text-xs font-serif tracking-widest2">LATEST NEWS</span>
          </div>
          <h1 className="text-cream-100 font-serif font-light text-4xl md:text-5xl tracking-wider">
            お知らせ
          </h1>
        </div>
      </section>

      {/* News List */}
      <section className="bg-cream-100 py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-1.5 text-xs font-serif tracking-widest rounded-sm border transition-colors duration-200 whitespace-nowrap cursor-pointer
                  ${activeCategory === cat
                    ? 'bg-forest-700 border-forest-700 text-cream-100'
                    : 'border-forest-600/30 text-forest-600 hover:bg-forest-50 hover:border-forest-500'
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Articles */}
          <div className="space-y-px">
            {filtered.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-forest-500/60 text-sm font-serif tracking-wider">
                  該当するお知らせはありません
                </p>
              </div>
            ) : (
              filtered.map((item) => (
                <article
                  key={item.id}
                  className="bg-white border border-cream-300 p-6 md:p-8 hover:bg-cream-50 transition-colors duration-200 group"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-5">
                    {/* Meta */}
                    <div className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2 flex-shrink-0">
                      <time className="text-forest-500/60 text-xs font-serif tracking-wider whitespace-nowrap">
                        {item.date}
                      </time>
                      <span className={`text-xs font-serif px-3 py-0.5 border rounded-sm whitespace-nowrap ${categoryColors[item.category] || 'border-forest-400/40 text-forest-500'}`}>
                        {item.category}
                      </span>
                    </div>
                    {/* Divider */}
                    <div className="hidden sm:block w-px self-stretch bg-cream-300 flex-shrink-0" />
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h2 className="text-forest-800 font-serif text-base tracking-wider mb-3 group-hover:text-forest-600 transition-colors duration-200 leading-relaxed">
                        {item.title}
                      </h2>
                      <p className="text-forest-600/70 text-sm font-serif leading-relaxed line-clamp-2">
                        {item.excerpt}
                      </p>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          {/* Count */}
          <div className="mt-8 text-right">
            <p className="text-forest-500/50 text-xs font-serif">
              {filtered.length} 件のお知らせ
            </p>
          </div>
        </div>
      </section>

      {/* Facebook Banner */}
      <section className="bg-cream-200 py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="border border-forest-600/25 bg-white p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="w-16 h-16 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-full h-full">
                <rect fill="#1877F2" height="32" rx="4" width="32"/>
                <path d="m18.4 10.5h2.1v-3.5h-2.4c-3 0-4.4 1.4-4.4 4.1v1.9h-2v3.5h2v8.5h3.5v-8.5h2.4l.4-3.5h-2.8v-1.5c0-.7.2-1 1.2-1z" fill="#ffffff"/>
              </svg>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-forest-500/60 text-xs font-serif tracking-widest mb-1">SNS</p>
              <h3 className="text-forest-800 font-serif font-medium text-lg tracking-wider mb-2">
                OMFES Facebookページ
              </h3>
              <p className="text-forest-600/70 text-sm font-serif leading-relaxed">
                最新情報・活動報告はFacebookページでも随時発信しています。<br className="hidden md:block" />
                ぜひフォローしてご覧ください。
              </p>
            </div>
            <a
              href="https://www.facebook.com/people/OMFES-東洋医学未来推進協会/61586276497652/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="px-7 py-3 bg-forest-700 text-cream-200 text-sm font-serif tracking-widest hover:bg-forest-600 transition-colors duration-200 rounded-sm whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              ページを見る
              <i className="ri-external-link-line ml-2 text-xs" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
